import React, { useState, useEffect } from "react";
import { styled } from "@mui/material/styles";
import {
    DataGridPremium,
    GridColDef,
    useGridApiRef,
    useKeepGroupedColumnsHidden,
    gridDetailPanelExpandedRowsContentCacheSelector,
    gridDetailPanelExpandedRowIdsSelector,
    GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    useGridSelector,
    useGridApiContext,
    GridRenderCellParams,
    Toolbar,
    ToolbarButton,
    QuickFilter,
    QuickFilterControl,
    QuickFilterClear,
    QuickFilterTrigger,
} from "@mui/x-data-grid-premium"; // Import GridColDef

import Tooltip from "@mui/material/Tooltip";

import TextField from "@mui/material/TextField";

// import IconButton from "@mui/material/IconButton";

import InputAdornment from "@mui/material/InputAdornment";

import Typography from "@mui/material/Typography";

import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";

/*eslint-disable*/

import { IntlProvider } from "react-intl";

// Utilities and configurations
import FastAPIClient from "../../../client";
import config from "../../../config";
import DisplayReport from "../../../components/LayoutComponents/DisplayReport";
import { FiChevronDown, FiSearch, FiX } from "react-icons/fi";
import { Dayjs } from "dayjs";

// Define types for the data you're working with
interface Client {
    client_id: string;
    client_name: string;
    city: string;
}

interface Report {
    report_id: string;
    created_at: string;
    client: Client | null;
    user: {
        first_name: string;
        last_name: string;
    };
    description: string;
}

interface User {
    user_id: string;
    warehouse_id: string;
    first_name: string;
    last_name: string;
    warehouse_name?: string | null;
}

const client = new FastAPIClient(config);

const getColumns = (
    expandedReports: { [key: string]: boolean },
    toggleReportRow: (reportId: string) => void
): GridColDef[] => [
    {
        field: "userFirstName",
        headerName: "Commercial",
        width: 150,
        filterable: true,
        sortable: true,
    },
    {
        field: "clientName",
        headerName: "Client",
        width: 150,
        filterable: true,
        sortable: true,
    },
    {
        field: "description",
        headerName: "Type de réunion",
        width: 100,
        filterable: true,
        sortable: true,
    },
    {
        field: "clientLink",
        headerName: "",
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) =>
            params.row.client?.client_id && (
                <Button
                    href={`/client/${params.row.client.client_id}`}
                    variant="outlined"
                    size="small"
                    sx={{
                        backgroundColor: "#ffffff",
                        color: "#000000",
                        borderColor: "#000000",
                        borderRadius: "20px",
                        textTransform: "none",
                        "&:hover": {
                            backgroundColor: "#f5f5f5",
                            borderColor: "#000000",
                        },
                        minWidth: "auto",
                        padding: "2px 12px",
                    }}
                >
                    Fiche Client
                </Button>
            ),
    },
    {
        field: "displayDate",
        headerName: "Date",
        width: 200,
        flex: 0,
        filterable: true,
        sortable: true,
    },
    {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        width: 200,
        renderCell: (params) => (
            <CustomDetailPanelToggle id={params.id} value={params.value} />
        ),
    },
];

function CustomDetailPanelToggle(
    props: Pick<GridRenderCellParams, "id" | "value">
) {
    const { id } = props;
    const apiRef = useGridApiContext();

    const contentCache = useGridSelector(
        apiRef,
        gridDetailPanelExpandedRowsContentCacheSelector
    );

    const expandedRowIds = useGridSelector(
        apiRef,
        gridDetailPanelExpandedRowIdsSelector
    );

    const isExpanded = expandedRowIds.has(id);
    const hasDetail = React.isValidElement(contentCache[id]);

    if (!hasDetail) {
        return null;
    }

    return (
        <Button
            variant="contained"
            size="small"
            sx={{
                backgroundColor: isExpanded ? "#000000" : "#C3FF36",
                color: isExpanded ? "#ffffff" : "#000000",
                borderRadius: "20px",
                textTransform: "none",
                minWidth: "auto",
                padding: "2px 12px",
                width: "100px",
            }}
        >
            {isExpanded ? "Fermer" : "Ouvrir"}
        </Button>
    );
}

type OwnerState = {
    expanded: boolean;
};

const StyledQuickFilter = styled(QuickFilter)({
    display: "grid",
    alignItems: "center",
    marginLeft: "auto",
});

const StyledToolbarButton = styled(ToolbarButton)<{ ownerState: OwnerState }>(
    ({ theme, ownerState }) => ({
        gridArea: "1 / 1",
        width: "min-content",
        height: "min-content",
        zIndex: 1,
        opacity: ownerState.expanded ? 0 : 1,
        pointerEvents: ownerState.expanded ? "none" : "auto",
        transition: theme.transitions.create(["opacity"]),
    })
);

const StyledTextField = styled(TextField)<{
    ownerState: OwnerState;
}>(({ theme, ownerState }) => ({
    gridArea: "1 / 1",
    overflowX: "clip",
    width: ownerState.expanded ? 260 : "var(--trigger-width)",
    opacity: ownerState.expanded ? 1 : 0,
    transition: theme.transitions.create(["width", "opacity"]),
}));

function CustomToolbar() {
    return (
        <Toolbar>
            <StyledQuickFilter>
                <QuickFilterTrigger
                    render={(triggerProps, state) => (
                        <Tooltip title="Search" enterDelay={0}>
                            <StyledToolbarButton
                                {...triggerProps}
                                ownerState={{ expanded: state.expanded }}
                                color="default"
                                aria-disabled={state.expanded}
                            >
                                <FiSearch fontSize="small" />
                            </StyledToolbarButton>
                        </Tooltip>
                    )}
                />
                <QuickFilterControl
                    render={({ ref, ...controlProps }, state) => (
                        <StyledTextField
                            {...controlProps}
                            ownerState={{ expanded: state.expanded }}
                            inputRef={ref}
                            aria-label="Search"
                            placeholder="Search..."
                            size="small"
                            slotProps={{
                                input: {
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <FiSearch fontSize="small" />
                                        </InputAdornment>
                                    ),
                                    endAdornment: state.value ? (
                                        <InputAdornment position="end">
                                            <QuickFilterClear
                                                edge="end"
                                                size="small"
                                                aria-label="Clear search"
                                                //   material={{ sx: { marginRight: -0.75 } }}
                                            >
                                                <FiX fontSize="small" />
                                            </QuickFilterClear>
                                        </InputAdornment>
                                    ) : null,
                                    ...controlProps.slotProps?.input,
                                },
                                ...controlProps.slotProps,
                            }}
                        />
                    )}
                />
            </StyledQuickFilter>
        </Toolbar>
    );
}

interface ReportsProps {
    selectedUser: string;
    selectedWarehouse: string;
    dateRange: [Dayjs | null, Dayjs | null];
    onUserChange: (value: string) => void;
    onWarehouseChange: (value: string) => void;
}

const Reports: React.FC<ReportsProps> = ({
    selectedUser,
    selectedWarehouse,
    dateRange,
    onUserChange,
    onWarehouseChange,
}) => {
    const [filteredReports, setFilteredReports] = useState<Report[]>([]);
    const [expandedReports, setExpandedReports] = useState<{
        [key: string]: boolean;
    }>({});
    const [reportLayout, setReportLayout] = useState<any[]>([]);
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const language = "fr";

    const getReportLayout = () => {
        client.getReportLayout().then((data) => {
            setReportLayout(data);
        });
    };

    const getUserName = () => {
        client.fetchUser().then((data: User) => {
            setUser(data);
        });
    };

    const fetchReportsData = (user: User) => {
        if (!user?.user_id) return;
        setIsLoading(true);

        // If a specific user is selected, only show their reports
        const selectedUserName =
            selectedUser !== "all" ? selectedUser : undefined;

        // Get the date range
        const [startDate, endDate] = dateRange;
        const formattedStartDate = startDate
            ? startDate.format("YYYY-MM-DD")
            : undefined;
        const formattedEndDate = endDate
            ? endDate.format("YYYY-MM-DD")
            : undefined;

        client
            .getReports(
                null,
                user.warehouse_id,
                selectedWarehouse !== "all" ? selectedWarehouse : undefined,
                selectedUserName,
                formattedStartDate,
                formattedEndDate
            )
            .then((data) => {
                const formattedReports = data.results.map((item: any) => {
                    const report = item.Report;
                    const date = new Date(report.created_at);
                    const formattedDate = date.toLocaleDateString("fr-FR", {
                        weekday: "long",
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                    });
                    // Capitalize the first letter of the day and month
                    const parts = formattedDate.split(" ");
                    if (parts.length >= 3) {
                        parts[0] =
                            parts[0].charAt(0).toUpperCase() +
                            parts[0].slice(1);
                        parts[1] =
                            parts[1].charAt(0).toUpperCase() +
                            parts[1].slice(1);
                    }
                    const displayDate = parts.join(" ");

                    return {
                        id: report.report_id,
                        userFirstName: `${report.user?.first_name} ${report.user?.last_name}`,
                        created_at: report.created_at,
                        displayDate: displayDate,
                        clientName: `${report.client?.client_name} , ${
                            report.client?.city || "N/A"
                        }`,
                        description: item.description,
                        ...report,
                    };
                });

                const sortedReports = formattedReports.sort(
                    (a: any, b: any) =>
                        new Date(b.created_at).getTime() -
                        new Date(a.created_at).getTime()
                );
                setFilteredReports(sortedReports);
                setIsLoading(false);
            })
            .catch((error) => {
                console.error("Error fetching reports:", error);
                setIsLoading(false);
            });
    };

    useEffect(() => {
        getUserName();
    }, []);

    useEffect(() => {
        if (user?.user_id) {
            fetchReportsData(user);
            getReportLayout();
        }
    }, [user, selectedWarehouse, selectedUser, dateRange]);

    const toggleReportRow = (reportId: string) => {
        setExpandedReports((prevState) => ({
            ...prevState,
            [reportId]: !prevState[reportId],
        }));
    };

    const apiRef = useGridApiRef();

    const initialState = useKeepGroupedColumnsHidden({
        apiRef,
        initialState: {
            rowGrouping: {
                model: ["userFirstName", "displayDate"],
            },
        },
    });

    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 8,
        page: 0,
    });

    return (
        <IntlProvider locale="fr">
            <div className=" ml-5  text-left md:mr-20 border border-gray-200 rounded-md p-4">
                <Typography variant="subtitle1" gutterBottom>
                    Aperçu des comptes rendu
                </Typography>
                <div
                    className="bg-white rounded-lg"
                    style={{ height: 580, width: "100%", overflow: "auto" }}
                >
                    {isLoading ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight="400px"
                        >
                            <CircularProgress />
                        </Box>
                    ) : filteredReports && filteredReports.length > 0 ? (
                        <DataGridPremium
                            pagination
                            paginationMode="client"
                            paginationModel={paginationModel}
                            onPaginationModelChange={setPaginationModel}
                            columns={getColumns(
                                expandedReports,
                                toggleReportRow
                            )}
                            rows={filteredReports}
                            disableRowSelectionOnClick
                            autoHeight
                            getRowHeight={(params) =>
                                expandedReports[params.id] ? 300 : undefined
                            }
                            getDetailPanelContent={({ row }) => (
                                <div className="p-4">
                                    <DisplayReport
                                        reportLayout={reportLayout}
                                        report={row}
                                        language={language}
                                    />
                                </div>
                            )}
                            getDetailPanelHeight={({ row }) => "auto"}
                            apiRef={apiRef}
                            rowGroupingColumnMode="multiple"
                            initialState={initialState}
                            slots={{ toolbar: CustomToolbar }}
                            showToolbar
                            pageSizeOptions={[8]}
                            sx={{
                                fontSize: "0.8rem",
                                "& .MuiDataGrid-row.hasNoReports": {
                                    backgroundColor: "#ffebee",
                                },
                            }}
                            getRowClassName={(params) => {
                                return params.row.hasNoReports
                                    ? "hasNoReports"
                                    : "";
                            }}
                        />
                    ) : (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            minHeight="400px"
                        >
                            <Typography variant="body1" color="textSecondary">
                                Aucun rapport trouvé
                            </Typography>
                        </Box>
                    )}
                </div>
            </div>
        </IntlProvider>
    );
};

export default Reports;
